/**
 * SuperAdmin panel — platform-level tenant yönetimi.
 * Yalnızca `user.is_super_admin === true` kullanıcılar erişebilir.
 * Backend `_require_super_admin` zaten fail-closed; frontend gizleme sadece UX.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Building2, Plus, Users, Package, ShieldAlert, Loader2, CheckCircle2, CalendarPlus, ArrowUpRight } from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

const STATUS_BADGES = {
  active: { label: "Aktif", cls: "bg-emerald-950/60 text-emerald-300 border-emerald-800" },
  trial: { label: "Deneme", cls: "bg-amber-950/60 text-amber-300 border-amber-800" },
  passive: { label: "Pasif", cls: "bg-slate-800/60 text-slate-300 border-slate-700" },
  suspended: { label: "Askıda", cls: "bg-rose-950/60 text-rose-300 border-rose-800" },
};

const PACKAGE_LIMITS = { starter: 5, pro: 25, enterprise: 100 };

export default function SuperAdmin() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openNew, setOpenNew] = useState(false);
  const [newTenantMode, setNewTenantMode] = useState("standard"); // "standard" | "demo"

  useEffect(() => {
    if (user && !user.is_super_admin) navigate("/");
  }, [user, navigate]);

  const load = async () => {
    setLoading(true);
    try {
      const [s, t] = await Promise.all([
        api.get("/super-admin/stats"),
        api.get("/super-admin/tenants"),
      ]);
      setStats(s.data);
      setTenants(t.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Yükleme hatası");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-8" data-testid="super-admin-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-100 tracking-tight flex items-center gap-3">
            <ShieldAlert className="w-7 h-7 text-blue-500" />
            Süper Yönetim Paneli
          </h1>
          <p className="text-sm text-slate-400 mt-1">
            Platform genelinde tüm şirket/kiracıları (tenant) yönet.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            data-testid="new-demo-tenant-btn"
            onClick={() => { setNewTenantMode("demo"); setOpenNew(true); }}
            variant="outline"
            className="border-emerald-700 bg-emerald-950/40 text-emerald-200 hover:bg-emerald-900/40 rounded-sm uppercase tracking-wider text-xs font-semibold"
          >
            <Package className="w-4 h-4 mr-2" />
            Demo Firma Oluştur
          </Button>
          <Button
            data-testid="new-tenant-btn"
            onClick={() => { setNewTenantMode("standard"); setOpenNew(true); }}
            className="bg-blue-600 hover:bg-blue-500 rounded-sm uppercase tracking-wider text-xs font-semibold"
          >
            <Plus className="w-4 h-4 mr-2" />
            Yeni Şirket Ekle
          </Button>
        </div>
      </div>

      {/* Stats KPIs */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <KpiCard testId="kpi-total" title="Toplam Şirket" value={stats.total} Icon={Building2} tint="orange" />
          <KpiCard testId="kpi-active" title="Aktif" value={stats.active} Icon={CheckCircle2} tint="emerald" />
          <KpiCard testId="kpi-trial" title="Deneme" value={stats.trial} Icon={Package} tint="amber" />
          <KpiCard testId="kpi-users" title="Toplam Kullanıcı" value={stats.total_users} Icon={Users} tint="slate" />
        </div>
      )}

      {/* Tenants Table */}
      <Card className="bg-slate-900/50 border-slate-800 rounded-sm">
        <CardHeader className="border-b border-slate-800 py-3">
          <CardTitle className="text-slate-100 uppercase text-xs tracking-widest font-semibold">
            Şirketler ({tenants.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-slate-500">
              <Loader2 className="w-6 h-6 animate-spin inline-block mr-2" />
              Yükleniyor…
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-slate-800">
                  <TableHead className="text-slate-400 text-xs uppercase">Firma</TableHead>
                  <TableHead className="text-slate-400 text-xs uppercase">Company ID</TableHead>
                  <TableHead className="text-slate-400 text-xs uppercase">Durum</TableHead>
                  <TableHead className="text-slate-400 text-xs uppercase">Paket</TableHead>
                  <TableHead className="text-slate-400 text-xs uppercase text-right">Kullanıcı</TableHead>
                  <TableHead className="text-slate-400 text-xs uppercase">Deneme Bitiş</TableHead>
                  <TableHead className="text-slate-400 text-xs uppercase">Oluşturuldu</TableHead>
                  <TableHead className="text-slate-400 text-xs uppercase">Eylem</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tenants.map((t) => {
                  const s = STATUS_BADGES[t.status] || STATUS_BADGES.passive;
                  return (
                    <TableRow key={t.id} data-testid={`tenant-row-${t.id}`} className="border-slate-800 hover:bg-slate-800/30">
                      <TableCell className="text-slate-100 font-medium">
                        {t.name}
                        {t.contact_email && <div className="text-xs text-slate-500 mt-1">{t.contact_email}</div>}
                      </TableCell>
                      <TableCell className="text-slate-300 font-mono text-xs">{t.company_id || t.id}</TableCell>
                      <TableCell>
                        <Badge className={`${s.cls} border font-medium rounded-sm`}>{s.label}</Badge>
                      </TableCell>
                      <TableCell className="text-slate-300 capitalize">
                        {t.package || "starter"}
                        {t.license_type && t.license_type !== "trial" && t.license_type !== "paid" && (
                          <span className="ml-2 px-1.5 py-0.5 text-[10px] rounded bg-slate-800 text-slate-400 uppercase tracking-wider">
                            {t.license_type}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-right text-slate-100 font-mono">
                        {t.user_count} / <span className="text-slate-500">{t.user_limit || 5}</span>
                      </TableCell>
                      <TableCell className="text-slate-400 text-xs">
                        {t.trial_ends_at ? new Date(t.trial_ends_at).toLocaleDateString("tr-TR") : "—"}
                      </TableCell>
                      <TableCell className="text-slate-400 text-xs">
                        {t.created_at ? new Date(t.created_at).toLocaleDateString("tr-TR") : "—"}
                      </TableCell>
                      <TableCell>
                        <TenantRowActions tenant={t} onDone={load} />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <SuperAdminRoleManager />

      <NewTenantDialog open={openNew} onOpenChange={setOpenNew} onCreated={load} presetMode={newTenantMode} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// FAZ 3 — Row Actions: 7/14/30 gün uzat + Demo → Ücretli dönüşüm
// ---------------------------------------------------------------------------
function TenantRowActions({ tenant, onDone }) {
  const [busy, setBusy] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const isTrialOrDemo = ["trial", "demo"].includes(tenant.license_type) || tenant.is_trial || tenant.status === "trial" || tenant.status === "suspended";

  const extend = async (days) => {
    setBusy(`ext${days}`);
    try {
      await api.post(`/super-admin/tenants/${tenant.id}/extend-trial`, { extra_days: days });
      toast.success(`Deneme ${days} gün uzatıldı`);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Uzatma başarısız");
    } finally { setBusy(null); }
  };

  const convertToPaid = async () => {
    if (!window.confirm(`${tenant.name} firmasını ücretli aboneye dönüştür?\n\n• company_id değişmez, tüm veri korunur.\n• Paket: ${tenant.package || "starter"}\n• Deneme kapatılır, aktif hâle geçer.`)) return;
    setBusy("paid");
    try {
      await api.post(`/super-admin/tenants/${tenant.id}/convert-to-paid`, {
        package: tenant.package || "starter",
        keep_demo_data: true,
      });
      toast.success("Ücretli aboneye dönüştürüldü");
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Dönüşüm başarısız");
    } finally { setBusy(null); }
  };

  if (tenant.license_type === "internal") {
    return (
      <div className="flex items-center gap-1">
        <Button
          data-testid={`edit-tenant-${tenant.id}`}
          size="sm" variant="outline"
          onClick={() => setEditOpen(true)}
          className="h-7 px-2 py-0 text-[10px] border-slate-700 bg-slate-900/40 text-slate-300 hover:bg-slate-800/40 rounded-sm"
        >
          Modüller
        </Button>
        <EditTenantDialog tenant={tenant} open={editOpen} onOpenChange={setEditOpen} onSaved={onDone} />
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1" data-testid={`tenant-actions-${tenant.id}`}>
      {isTrialOrDemo && (
        <>
          <Button
            data-testid={`extend-7-${tenant.id}`}
            size="sm" variant="outline"
            disabled={!!busy}
            onClick={() => extend(7)}
            className="h-7 px-2 py-0 text-[10px] border-amber-700 bg-amber-950/40 text-amber-200 hover:bg-amber-900/40 rounded-sm"
            title="7 gün uzat"
          >
            {busy === "ext7" ? <Loader2 className="w-3 h-3 animate-spin" /> : "+7"}
          </Button>
          <Button
            data-testid={`extend-14-${tenant.id}`}
            size="sm" variant="outline"
            disabled={!!busy}
            onClick={() => extend(14)}
            className="h-7 px-2 py-0 text-[10px] border-amber-700 bg-amber-950/40 text-amber-200 hover:bg-amber-900/40 rounded-sm"
            title="14 gün uzat"
          >
            {busy === "ext14" ? <Loader2 className="w-3 h-3 animate-spin" /> : "+14"}
          </Button>
          <Button
            data-testid={`extend-30-${tenant.id}`}
            size="sm" variant="outline"
            disabled={!!busy}
            onClick={() => extend(30)}
            className="h-7 px-2 py-0 text-[10px] border-amber-700 bg-amber-950/40 text-amber-200 hover:bg-amber-900/40 rounded-sm"
            title="30 gün uzat"
          >
            {busy === "ext30" ? <Loader2 className="w-3 h-3 animate-spin" /> : "+30"}
          </Button>
          <Button
            data-testid={`convert-paid-${tenant.id}`}
            size="sm"
            disabled={!!busy}
            onClick={convertToPaid}
            className="h-7 px-2 py-0 text-[10px] bg-emerald-700 hover:bg-emerald-600 text-white rounded-sm"
            title="Ücretli aboneye dönüştür"
          >
            {busy === "paid" ? <Loader2 className="w-3 h-3 animate-spin" /> : <><ArrowUpRight className="w-3 h-3 mr-0.5" />Ücretli</>}
          </Button>
        </>
      )}
      <Button
        data-testid={`edit-tenant-${tenant.id}`}
        size="sm" variant="outline"
        onClick={() => setEditOpen(true)}
        className="h-7 px-2 py-0 text-[10px] border-slate-700 bg-slate-900/40 text-slate-300 hover:bg-slate-800/40 rounded-sm"
        title="Modül ve durum düzenle"
      >
        Modüller
      </Button>
      <EditTenantDialog tenant={tenant} open={editOpen} onOpenChange={setEditOpen} onSaved={onDone} />
    </div>
  );
}


// ---------------------------------------------------------------------------
// FAZ 3 M5 — ModuleSelector: mevcut ERP MODULES registry'sinden checkbox listesi
// ---------------------------------------------------------------------------
function ModuleSelector({ modules = [], value, onChange }) {
  const allOpen = !value || value.length === 0;  // null / empty → hepsi açık
  const selected = new Set(allOpen ? modules.map((m) => m.code) : value);

  const toggle = (code) => {
    const next = new Set(selected);
    if (next.has(code)) next.delete(code);
    else next.add(code);
    if (next.size === modules.length) {
      onChange(null);  // hepsi seçilmiş → null (hepsi açık semantic'i)
    } else {
      onChange(Array.from(next));
    }
  };

  const selectAll = () => onChange(null);
  const clearAll = () => onChange([]);

  return (
    <div className="space-y-3" data-testid="module-selector">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-slate-100 text-sm uppercase tracking-wider">Modül İzinleri</div>
          <div className="text-[11px] text-slate-500">
            {allOpen ? "Tüm modüller açık" : `${value.length}/${modules.length} modül açık`}
            {" · "}Modül kapatıldığında veri SİLİNMEZ, sadece erişim engellenir.
          </div>
        </div>
        <div className="flex gap-1">
          <Button type="button" size="sm" variant="outline"
                  data-testid="module-selector-all"
                  onClick={selectAll}
                  className="h-7 px-2 py-0 text-[10px] border-slate-700 bg-slate-800/40 text-slate-300 hover:bg-slate-700/40 rounded-sm">
            Hepsi
          </Button>
          <Button type="button" size="sm" variant="outline"
                  data-testid="module-selector-none"
                  onClick={clearAll}
                  className="h-7 px-2 py-0 text-[10px] border-slate-700 bg-slate-800/40 text-slate-300 hover:bg-slate-700/40 rounded-sm">
            Hiçbiri
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5 max-h-64 overflow-y-auto pr-1">
        {modules.map((m) => {
          const on = selected.has(m.code);
          return (
            <label
              key={m.code}
              data-testid={`module-cb-${m.code}`}
              className={`flex items-center gap-2 rounded-sm border px-2 py-1.5 cursor-pointer transition-colors ${
                on
                  ? "border-blue-700/60 bg-blue-950/30 text-blue-100"
                  : "border-slate-800 bg-slate-950/40 text-slate-400 hover:bg-slate-900/40"
              }`}
            >
              <input
                type="checkbox"
                checked={on}
                onChange={() => toggle(m.code)}
                className="accent-blue-500 h-3.5 w-3.5"
              />
              <span className="text-xs truncate">{m.label || m.code}</span>
            </label>
          );
        })}
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// FAZ 3 M5 — EditTenantDialog: mevcut firma için modül izinleri + statü
// ---------------------------------------------------------------------------
function EditTenantDialog({ tenant, open, onOpenChange, onSaved }) {
  const [modules, setModules] = useState([]);
  const [allowed, setAllowed] = useState(tenant?.allowed_modules ?? null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setAllowed(tenant?.allowed_modules ?? null);
    api.get("/meta/modules").then((r) => setModules(r.data?.modules || [])).catch(() => {});
  }, [open, tenant]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/super-admin/tenants/${tenant.id}/allowed-modules`, {
        allowed_modules: allowed,
      });
      toast.success("Modül izinleri güncellendi");
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kaydetme başarısız");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="edit-tenant-dialog" className="bg-slate-900 border-slate-800 rounded-sm max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-slate-100 uppercase tracking-widest text-sm">
            {tenant?.name} — Modül İzinleri
          </DialogTitle>
          <p className="text-xs text-slate-500 mt-1">
            company_id: <span className="font-mono text-slate-400">{tenant?.company_id}</span>
            {" · "}Lisans: <span className="text-slate-400">{tenant?.license_type || "—"}</span>
          </p>
        </DialogHeader>
        <div className="space-y-5">
          <div className="rounded-sm border border-amber-800/60 bg-amber-950/30 p-3 text-xs text-amber-200">
            <strong>Bilgi:</strong> Bir modülü kapatmak <strong>veriyi silmez</strong>. Yalnızca kullanıcıların
            frontend menüsünden gizlenir ve backend API erişimi engellenir. Modül yeniden açıldığında veriler
            aynen görünür.
          </div>
          <ModuleSelector modules={modules} value={allowed} onChange={setAllowed} />
        </div>
        <DialogFooter className="gap-2 mt-4">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}
                  className="border-slate-700 bg-transparent text-slate-300 hover:bg-slate-800 rounded-sm">
            İptal
          </Button>
          <Button type="button" onClick={save} disabled={saving}
                  data-testid="edit-tenant-save"
                  className="bg-blue-600 hover:bg-blue-500 rounded-sm uppercase tracking-wider text-xs font-semibold">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Kaydet"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ---------------------------------------------------------------------------
// Süper Admin Rolü Yönetimi — kullanıcıyı is_super_admin bayrağıyla işaretle
// ---------------------------------------------------------------------------
function SuperAdminRoleManager() {
  const [email, setEmail] = useState("");
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState([]);
  const [supers, setSupers] = useState([]);
  const [busy, setBusy] = useState(null);

  const loadSupers = async () => {
    try {
      const r = await api.get("/super-admin/users/super-admins");
      setSupers(r.data.super_admins || []);
    } catch { /* ignore */ }
  };
  useEffect(() => { loadSupers(); }, []);

  const search = async () => {
    if (!email.trim()) { setResults([]); return; }
    setSearching(true);
    try {
      const r = await api.get(`/super-admin/users/search`, { params: { email: email.trim() } });
      setResults(r.data.users || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Arama başarısız");
    } finally { setSearching(false); }
  };

  const promote = async (targetEmail) => {
    setBusy(targetEmail);
    try {
      const r = await api.post("/super-admin/users/promote", { email: targetEmail });
      if (r.data.already_super) toast.info("Zaten Süper Admin");
      else toast.success(`${targetEmail} Süper Admin yapıldı`);
      await loadSupers();
      await search();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Promote başarısız");
    } finally { setBusy(null); }
  };

  const demote = async (targetEmail) => {
    if (!window.confirm(`${targetEmail} kullanıcısının süper admin yetkisini kaldırmak istediğinize emin misiniz?`)) return;
    setBusy(targetEmail);
    try {
      const r = await api.post("/super-admin/users/demote", { email: targetEmail });
      if (r.data.already_regular) toast.info("Zaten normal kullanıcı");
      else toast.success(`${targetEmail} artık normal kullanıcı`);
      await loadSupers();
      await search();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Demote başarısız");
    } finally { setBusy(null); }
  };

  return (
    <Card className="bg-slate-900/50 border-slate-800 rounded-sm" data-testid="super-admin-role-manager">
      <CardHeader className="border-b border-slate-800 py-3">
        <CardTitle className="text-slate-100 uppercase text-xs tracking-widest font-semibold flex items-center gap-2">
          <ShieldAlert className="w-4 h-4 text-blue-400" />
          Süper Admin Rolü Yönetimi
        </CardTitle>
      </CardHeader>
      <CardContent className="p-5 space-y-4">
        <div className="rounded-sm border border-amber-800/60 bg-amber-950/30 p-3 text-xs text-amber-200">
          <strong>Dikkat:</strong> Süper Admin, tüm tenant'lara erişebilir, veri migrasyonu ve kurtarma paneli gibi
          hassas araçları çalıştırabilir. Yalnızca güvendiğiniz kişilere verin.
        </div>

        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono mb-2">
            Mevcut Süper Adminler ({supers.length})
          </div>
          <div className="space-y-1">
            {supers.map((s) => (
              <div key={s.id} className="flex items-center justify-between rounded-sm border border-slate-800 bg-slate-950/40 px-3 py-2">
                <div className="text-xs">
                  <div className="text-slate-100 font-medium">{s.email}</div>
                  <div className="text-slate-500">{s.name || "—"} · {s.company_id || "—"}</div>
                </div>
                <Button size="sm" variant="outline"
                        className="border-rose-800/60 text-rose-300 hover:bg-rose-950/40 rounded-sm text-xs uppercase tracking-wider"
                        disabled={busy === s.email}
                        onClick={() => demote(s.email)}
                        data-testid={`demote-btn-${s.email}`}>
                  {busy === s.email ? <Loader2 className="w-3 h-3 animate-spin" /> : "Kaldır"}
                </Button>
              </div>
            ))}
            {supers.length === 0 && <div className="text-xs text-slate-500 text-center py-2">—</div>}
          </div>
        </div>

        <div className="border-t border-slate-800 pt-4">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono mb-2">
            Kullanıcı Ara ve Süper Admin Yap
          </div>
          <div className="flex gap-2">
            <Input
              placeholder="E-posta veya email parçası (örn: ugur)"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") search(); }}
              className="bg-slate-950 border-slate-700 text-slate-100 text-xs rounded-sm"
              data-testid="promote-search-input"
            />
            <Button onClick={search} disabled={searching || !email.trim()}
                    className="bg-slate-700 hover:bg-slate-600 rounded-sm text-xs uppercase tracking-wider"
                    data-testid="promote-search-btn">
              {searching ? <Loader2 className="w-3 h-3 animate-spin" /> : "Ara"}
            </Button>
          </div>
          <div className="mt-2 space-y-1">
            {results.map((u) => (
              <div key={u.id} className="flex items-center justify-between rounded-sm border border-slate-800 bg-slate-950/40 px-3 py-2">
                <div className="text-xs min-w-0 flex-1">
                  <div className="text-slate-100 font-medium truncate">{u.email}</div>
                  <div className="text-slate-500 truncate">{u.name || "—"} · {u.role || "—"} · {u.company_id || "—"}</div>
                </div>
                {u.is_super_admin ? (
                  <Badge className="bg-emerald-950/60 text-emerald-300 border-emerald-800 rounded-sm text-[10px]">SÜPER ADMİN</Badge>
                ) : (
                  <Button size="sm"
                          className="bg-blue-600 hover:bg-blue-500 rounded-sm text-xs uppercase tracking-wider"
                          disabled={busy === u.email}
                          onClick={() => promote(u.email)}
                          data-testid={`promote-btn-${u.email}`}>
                    {busy === u.email ? <Loader2 className="w-3 h-3 animate-spin" /> : "Süper Admin Yap"}
                  </Button>
                )}
              </div>
            ))}
            {email && !searching && results.length === 0 && (
              <div className="text-xs text-slate-500 text-center py-2">Arama sonucu yok</div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function KpiCard({ testId, title, value, Icon, tint }) {
  const tints = {
    orange: "text-blue-500 border-blue-800/50",
    emerald: "text-emerald-500 border-emerald-800/50",
    amber: "text-amber-500 border-amber-800/50",
    slate: "text-slate-400 border-slate-700",
  };
  return (
    <Card data-testid={testId} className={`bg-slate-900/50 border ${tints[tint] || tints.slate} rounded-sm`}>
      <CardContent className="p-5">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-500">{title}</div>
            <div className={`text-3xl font-bold mt-2 ${tints[tint]?.split(" ")[0]}`}>{value}</div>
          </div>
          <Icon className={`w-8 h-8 ${tints[tint]?.split(" ")[0]}`} />
        </div>
      </CardContent>
    </Card>
  );
}

function NewTenantDialog({ open, onOpenChange, onCreated, presetMode = "standard" }) {
  const isDemoPreset = presetMode === "demo";
  const empty = {
    name: "", slug: "", tax_no: "", contact_name: "", contact_email: "", contact_phone: "",
    package: "starter", user_limit: 5, is_trial: true, trial_days: 14,
    license_type: isDemoPreset ? "demo" : "trial",
    seed_demo_data: isDemoPreset,
    allowed_modules: null,  // null → hepsi açık
    admin_email: "", admin_password: "", admin_name: "",
  };
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState([]);
  const [modules, setModules] = useState([]);

  useEffect(() => {
    if (!open) return;
    // Modül listesini çek
    api.get("/meta/modules").then((r) => setModules(r.data?.modules || [])).catch(() => {});
  }, [open]);

  useEffect(() => {
    if (!open) { setForm(empty); setErrors([]); }
    else if (isDemoPreset) {
      // Demo preset: 14g trial + seed. Modül varsayılanı: demo için sınırlı set
      setForm((f) => ({
        ...f, license_type: "demo", seed_demo_data: true, is_trial: true, trial_days: 14,
        allowed_modules: ["dashboard", "customers", "orders", "products", "warehouse", "reports", "settings"],
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isDemoPreset]);
  useEffect(() => {
    setForm((f) => ({ ...f, user_limit: PACKAGE_LIMITS[f.package] || 5 }));
  }, [form.package]);

  const submit = async (e) => {
    e.preventDefault();
    const miss = [];
    if (!form.name.trim()) miss.push("Firma Adı");
    if (!form.slug.trim()) miss.push("Slug");
    if (!form.contact_name.trim()) miss.push("Yetkili");
    if (!form.contact_email.trim()) miss.push("İletişim E-postası");
    if (!form.admin_email.trim()) miss.push("Admin E-postası");
    if (!form.admin_password || form.admin_password.length < 8) miss.push("Admin Şifresi (min 8)");
    if (!form.admin_name.trim()) miss.push("Admin Adı");
    if (miss.length) { setErrors(miss); return; }
    setErrors([]);
    setSaving(true);
    try {
      const { data } = await api.post("/super-admin/tenants", form);
      toast.success(`Şirket oluşturuldu: ${data.name} (${data.company_id})`);
      onOpenChange(false);
      onCreated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Oluşturma hatası");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="new-tenant-dialog" className="bg-slate-900 border-slate-800 rounded-sm max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-slate-100 uppercase tracking-widest text-sm">
            {isDemoPreset ? "Demo Firma Oluştur" : "Yeni Şirket / Kiracı"}
          </DialogTitle>
          {isDemoPreset && (
            <p className="text-xs text-emerald-400 mt-1">
              Demo şablonu: 14 gün süreli, örnek veri otomatik yüklenir. Süre bitince Süper Admin ile uzatılır.
            </p>
          )}
        </DialogHeader>
        <form onSubmit={submit} className="space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-slate-300 text-xs uppercase">Firma Adı *</Label>
              <Input data-testid="new-tenant-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-slate-950 border-slate-700" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300 text-xs uppercase">Slug (Tenant ID) *</Label>
              <Input data-testid="new-tenant-slug" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") })} placeholder="ornek-tekstil" className="bg-slate-950 border-slate-700 font-mono" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-slate-300 text-xs uppercase">Vergi No</Label>
              <Input value={form.tax_no} onChange={(e) => setForm({ ...form, tax_no: e.target.value })} className="bg-slate-950 border-slate-700" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300 text-xs uppercase">Yetkili Kişi *</Label>
              <Input data-testid="new-tenant-contact" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} className="bg-slate-950 border-slate-700" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-slate-300 text-xs uppercase">İletişim E-postası *</Label>
              <Input type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} className="bg-slate-950 border-slate-700" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300 text-xs uppercase">Telefon</Label>
              <Input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} className="bg-slate-950 border-slate-700" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label className="text-slate-300 text-xs uppercase">Paket</Label>
              <Select value={form.package} onValueChange={(v) => setForm({ ...form, package: v })}>
                <SelectTrigger data-testid="new-tenant-package" className="bg-slate-950 border-slate-700"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="starter">Starter</SelectItem><SelectItem value="pro">Pro</SelectItem><SelectItem value="enterprise">Enterprise</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300 text-xs uppercase">Kullanıcı Limiti</Label>
              <Input type="number" min={1} value={form.user_limit} onChange={(e) => setForm({ ...form, user_limit: parseInt(e.target.value) || 1 })} className="bg-slate-950 border-slate-700" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300 text-xs uppercase">Deneme Süresi (gün)</Label>
              <Input type="number" min={0} disabled={!form.is_trial} value={form.trial_days} onChange={(e) => setForm({ ...form, trial_days: parseInt(e.target.value) || 14 })} className="bg-slate-950 border-slate-700" />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Switch data-testid="new-tenant-trial" checked={form.is_trial} onCheckedChange={(v) => setForm({ ...form, is_trial: v })} />
            <Label className="text-slate-300">Deneme Sürümü</Label>
          </div>

          <div className="grid grid-cols-2 gap-4 border-t border-slate-800 pt-4">
            <div className="space-y-2">
              <Label className="text-slate-300 text-xs uppercase">Lisans Tipi</Label>
              <Select value={form.license_type} onValueChange={(v) => setForm({ ...form, license_type: v })}>
                <SelectTrigger data-testid="new-tenant-license-type" className="bg-slate-950 border-slate-700"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="trial">Trial (Deneme)</SelectItem>
                  <SelectItem value="demo">Demo (14g + örnek veri)</SelectItem>
                  <SelectItem value="pilot">Pilot (Ücretsiz, tam paket)</SelectItem>
                  <SelectItem value="internal">Internal (Fucan iç)</SelectItem>
                  <SelectItem value="paid">Ücretli (Aktif abone)</SelectItem>
                </SelectContent>
              </Select>
              <div className="text-[11px] text-slate-500 leading-tight">
                {form.license_type === "demo" && "14 gün süreli deneme + gerçekçi örnek veri."}
                {form.license_type === "pilot" && "0₺, tam paket. Trial değil, aktif."}
                {form.license_type === "internal" && "Fucan iç instance'ı. Limit/trial uygulanmaz."}
                {form.license_type === "paid" && "Ücretli abone; trial değil."}
                {form.license_type === "trial" && "Standart deneme (yukarıdaki süreye göre)."}
              </div>
            </div>
            {form.license_type === "demo" && (
              <div className="flex items-center gap-3 pt-6">
                <Switch data-testid="new-tenant-seed-demo" checked={form.seed_demo_data}
                        onCheckedChange={(v) => setForm({ ...form, seed_demo_data: v })} />
                <Label className="text-slate-300 text-xs">Örnek veriyi otomatik seed et
                  <span className="block text-slate-500 normal-case">15 müşteri · 30 ürün · 8 personel · 5 sipariş · 3 tahsilat</span>
                </Label>
              </div>
            )}
          </div>

          <div className="border-t border-slate-800 pt-4">
            <ModuleSelector
              modules={modules}
              value={form.allowed_modules}
              onChange={(next) => setForm((f) => ({ ...f, allowed_modules: next }))}
            />
          </div>

          <div className="border-t border-slate-800 pt-4">
            <h3 className="text-slate-100 text-sm uppercase tracking-wider mb-4">İlk Yönetici</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300 text-xs uppercase">Admin Adı *</Label>
                <Input value={form.admin_name} onChange={(e) => setForm({ ...form, admin_name: e.target.value })} className="bg-slate-950 border-slate-700" />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300 text-xs uppercase">Admin E-postası *</Label>
                <Input data-testid="new-tenant-admin-email" type="email" value={form.admin_email} onChange={(e) => setForm({ ...form, admin_email: e.target.value })} className="bg-slate-950 border-slate-700" />
              </div>
            </div>
            <div className="space-y-2 mt-4">
              <Label className="text-slate-300 text-xs uppercase">Admin Şifresi * (min 8 karakter)</Label>
              <Input data-testid="new-tenant-admin-password" type="password" value={form.admin_password} onChange={(e) => setForm({ ...form, admin_password: e.target.value })} className="bg-slate-950 border-slate-700" />
              <p className="text-xs text-slate-500">Admin ilk girişte şifresini değiştirmeye zorlanacak.</p>
            </div>
          </div>

          <DialogFooter className="gap-2">
            {errors.length > 0 && (
              <div data-testid="new-tenant-error" className="mr-auto flex items-center gap-2 rounded-sm border border-rose-800 bg-rose-950/60 px-3 py-2 text-xs text-rose-200">
                <ShieldAlert className="w-4 h-4 shrink-0" />
                Zorunlu: {errors.join(", ")}
              </div>
            )}
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="border-slate-700 bg-transparent text-slate-300 hover:bg-slate-800 rounded-sm">İptal</Button>
            <Button data-testid="new-tenant-save" type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-500 rounded-sm uppercase tracking-wider text-xs font-semibold">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Şirketi Oluştur"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
